import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import { getMe, updateProfile } from "../../../api/profile";
import { getUserProjects } from "../../../api/projects";

import PersonalDetails from "../../elements/profile/PersonalDetails";
import ProjectCard from "../../cards/ProjectCard";
import Pagination from "../../elements/Pagination";

import { ReactComponent as User } from "../../../resources/icons/user.svg";
import { ReactComponent as Edit } from "../../../resources/icons/edit.svg";
import { ReactComponent as Logout } from "../../../resources/icons/logout.svg";
import { ReactComponent as Phone } from "../../../resources/icons/phone.svg";
import { ReactComponent as Email } from "../../../resources/icons/mail.svg";
import { preventCachedImage } from "../../../utils/helperFunctions";
import { toast } from "react-toastify";
import { useAuthContext } from "../../../contexts/authContext";
import LogoutModal from "../../elements/profile/LogoutModal";
import EditProfileModal from "../../elements/profile/EditProfileModal";

interface Project {
  id: number;
  title: string;
  summary: string;
  status: string;
  publishedAt: Date;
  createdAt: Date;
  closedAt: string;
  address: string;
}

const AssignorProfile: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState({
    id: 0,
    name: "",
    email: "",
    type: "",
    image: "",
    phone: "",
  });

  const [allProjectsInfo, setAllProjectsInfo] = useState<Project[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const rowsPerPage = 6;
  const hasFetchedUser = useRef(false);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const { logout, loggingOut, setUser: setContextUser } = useAuthContext();

  useEffect(() => {
    const fetchUser = async () => {
      if (hasFetchedUser.current) return;
      try {
        const user = await getMe();
        setUser(user);
        hasFetchedUser.current = true;
      } catch {}
    };
    fetchUser();
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      if (!user.id) return;
      try {
        const data = await getUserProjects(user.id, currentPage, rowsPerPage);
        const filteredProjects = data.projects.filter((project: Project) =>
          ["OPEN", "COMPLETED", "CANCELLED"].includes(project.status)
        );
        setAllProjectsInfo(filteredProjects);
        setTotalPages(data.totalPages || 1);
      } catch {}
    };
    fetchProjects();
  }, [user.id, currentPage]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex flex-row">
      <div className="flex-1 ml-[17.5rem]">
        <h1 className="p-6 text-2xl font-bold">My profile</h1>
        <div className="px-6 py-12 bg-[#F9FAFB] flex flex-row justify-between">
          <div className="flex flex-row">
            <div className="mr-4 self-center">
              {user.image ? (
                <img
                  className="w-[80px] h-[80px] object-cover rounded-full"
                  src={preventCachedImage(user.image)}
                  alt="Profile"
                />
              ) : (
                <User className="self-center" />
              )}
            </div>
            <div className="flex flex-col self-center">
              <h3 className="mb-1 font-bold text-xl">{user.name}</h3>
              <div>
                <span className="capitalize px-3 py-1 rounded-full text-xs font-medium border shrink-0 text-[#B54708] bg-[#FFFAEB] border-[#FEDF89]">
                  {user.type.charAt(0).toUpperCase() +
                    user.type.slice(1).toLowerCase()}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-col">
            <button
              type="button"
              onClick={() => setIsEditModalOpen(true)}
              className="text-sm mb-2 flex flex-row px-4 py-2 bg-white border rounded-md font-semibold"
            >
              <Edit className="mr-2" />
              Edit profile
            </button>
            <button
              type="button"
              onClick={() => setIsLogoutModalOpen(true)}
              className="text-sm mb-2 flex flex-row px-4 py-2 bg-white border rounded-md font-semibold"
            >
              <Logout className="w-5 mr-2" style={{ color: "white" }} />
              Logout
            </button>
          </div>
        </div>

        <div className="p-6">
          <h3 className="text-lg font-bold mb-4">Personal details</h3>
          <div className="max-w-screen flex flex-wrap gap-x-12 gap-y-4">
            <PersonalDetails
              label="Phone number"
              Icon={Phone}
              info={user.phone}
            />
            <PersonalDetails label="Email" Icon={Email} info={user.email} />
          </div>

          {allProjectsInfo.length > 0 && (
            <>
              <h3 className="text-lg font-bold mb-4 mt-8">Projects</h3>
              <div className="max-w-screen grid xl:grid-cols-3 lg:grid-cols-2 grid-cols-1 gap-4">
                {allProjectsInfo.map((project) => (
                  <ProjectCard
                    key={project.id}
                    id={project.id}
                    title={project.title}
                    publishedAt={
                      project.publishedAt
                        ? dayjs(project.publishedAt).format("DD.MM.YYYY")
                        : dayjs(project.createdAt).format("DD.MM.YYYY")
                    }
                    status={project.status.toLowerCase()}
                    summary={project.summary || "No summary available."}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="border-t mt-6 pt-4">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <EditProfileModal
        initialValues={{
          name: user.name,
          email: user.email,
          phone: user.phone,
          image: user.image,
        }}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={async (values) => {
          try {
            const updatedUser = await updateProfile(values);
            setUser(updatedUser);
            setContextUser(updatedUser);
            setIsEditModalOpen(false);
            toast.success("Profile updated successfully");
          } catch (err: any) {
            toast.error("Failed to update profile");
          }
        }}
        userRole="ASSIGNOR"
      />

      <LogoutModal
        isOpen={isLogoutModalOpen}
        closeModal={() => setIsLogoutModalOpen(false)}
        handleLogout={handleLogout}
        loading={loggingOut}
      />
    </div>
  );
};

export default AssignorProfile;
